// src/components/JalaliDatePicker.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import "./JalaliDatePicker.css";

/* ============ توابع کمکی تقویم شمسی ============ */
const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];

const toFa = (n) => String(n).replace(/\d/g, (d) => FA_DIGITS[+d]);
const toEn = (str) =>
  String(str).replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));

function g2j(gy, gm, gd) {
  const g_d_m = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
  let jy = gy <= 1600 ? 0 : 979;
  gy -= gy <= 1600 ? 621 : 1600;
  const gy2 = gm > 2 ? gy + 1 : gy;
  let days =
    365 * gy +
    Math.floor((gy2 + 3) / 4) -
    Math.floor((gy2 + 99) / 100) +
    Math.floor((gy2 + 399) / 400) -
    80 +
    gd +
    g_d_m[gm - 1];
  jy += 33 * Math.floor(days / 12053);
  days %= 12053;
  jy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    jy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  const jm =
    days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
  return { jy, jm, jd };
}

function isLeapJalali(jy) {
  const r = jy % 33;
  return [1, 5, 9, 13, 17, 22, 26, 30].includes(r);
}

function jalaliMonthDays(jy, jm) {
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  return isLeapJalali(jy) ? 30 : 29;
}

/* بازه‌ی مجاز سال‌های شمسی برای ولیدیشن ورودی دستی */
const MIN_JYEAR = 1300;
const MAX_JYEAR = 1500;

function isValidJalali(y, m, d) {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d))
    return false;
  if (y < MIN_JYEAR || y > MAX_JYEAR) return false;
  if (m < 1 || m > 12) return false;
  if (d < 1 || d > jalaliMonthDays(y, m)) return false;
  return true;
}

function formatDate(y, m, d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${y}/${pad(m)}/${pad(d)}`;
}

/* ============ نام ماه‌ها و روزها ============ */
const MONTHS = [
  "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
  "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند",
];
const WEEKDAYS = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

/* ============ کامپوننت اصلی ============ */
export default function JalaliDatePicker({
  value = "",
  onChange,
  placeholder = "انتخاب تاریخ",
  name = "date",
  required = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const [error, setError] = useState("");
  // پنجره فعال پاپ‌اور: روزها | ماه‌ها | سال‌ها
  const [pickerMode, setPickerMode] = useState("days");
  const [yearRangeStart, setYearRangeStart] = useState(null);
  const wrapRef = useRef(null);

  // تاریخ امروز شمسی
  const today = useMemo(() => {
    const now = new Date();
    return g2j(now.getFullYear(), now.getMonth() + 1, now.getDate());
  }, []);

  const [viewYear, setViewYear] = useState(today.jy);
  const [viewMonth, setViewMonth] = useState(today.jm);

  // مقدار ورودی → نمایش فارسی
  useEffect(() => {
    if (!value) {
      setInputText("");
      setError("");
      return;
    }
    const match = toEn(value).match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (match) {
      setInputText(
        `${toFa(match[1])}/${toFa(match[2].padStart(2, "0"))}/${toFa(
          match[3].padStart(2, "0")
        )}`
      );
      setError("");
    } else {
      setInputText(value);
    }
  }, [value]);

  // بستن با کلیک بیرون
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setPickerMode("days");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ============== باز کردن تقویم ============== */
  const openCalendar = () => {
    setPickerMode("days");
    const match = value && toEn(value).match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (match) {
      setViewYear(+match[1]);
      setViewMonth(+match[2]);
    }
    setOpen(true);
  };

  /* ============== انتخاب روز ============== */
  // y و m اختیاری‌اند تا وقتی از دکمه‌ی «امروز» صدا زده می‌شود
  // دچار مشکل state بروزنشده (closure) نشویم.
  const selectDay = (day, y = viewYear, m = viewMonth) => {
    setError("");
    onChange?.(formatDate(y, m, day));
    setOpen(false);
    setPickerMode("days");
  };

  const selectMonth = (m) => {
    setViewMonth(m);
    setPickerMode("days");
  };

  const selectYear = (y) => {
    setViewYear(y);
    setPickerMode("days");
  };

  const openMonthPicker = () => setPickerMode("months");
  const openYearPicker = () => {
    setYearRangeStart(viewYear - 7);
    setPickerMode("years");
  };

  /* ============== ناوبری قبل/بعد (بسته به حالت فعال) ============== */
  const goPrev = () => {
    if (pickerMode === "years") {
      setYearRangeStart((s) => (s ?? viewYear) - 16);
      return;
    }
    if (pickerMode === "months") {
      setViewYear((y) => y - 1);
      return;
    }
    if (viewMonth === 1) {
      setViewMonth(12);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goNext = () => {
    if (pickerMode === "years") {
      setYearRangeStart((s) => (s ?? viewYear) + 16);
      return;
    }
    if (pickerMode === "months") {
      setViewYear((y) => y + 1);
      return;
    }
    if (viewMonth === 12) {
      setViewMonth(1);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  // رفتن به امروز + انتخاب مستقیم تاریخ امروز
  const goToday = () => {
    setViewYear(today.jy);
    setViewMonth(today.jm);
    selectDay(today.jd, today.jy, today.jm);
  };

  /* ============== ورودی دستی با ولیدیشن ============== */
  const handleInputChange = (e) => {
    // فقط رقم (فارسی/انگلیسی) و / مجاز است، حداکثر طول ۱۰ کاراکتر (۱۴۰۲/۰۱/۰۱)
    let raw = e.target.value.replace(/[^0-9۰-۹/]/g, "");
    if (raw.length > 10) raw = raw.slice(0, 10);
    setInputText(raw);

    const normalized = toEn(raw);
    const match = normalized.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);

    if (!match) {
      // هنوز کاربر در حال تایپ است؛ فقط اگر شکل تاریخ به‌هم‌ریخته باشد خطا نشان بده
      if (normalized.replace(/\//g, "").length >= 8) {
        setError("فرمت تاریخ باید مثل ۱۴۰۲/۰۱/۰۱ باشد");
      } else {
        setError("");
      }
      return;
    }

    const y = +match[1];
    const m = +match[2];
    const d = +match[3];

    if (!isValidJalali(y, m, d)) {
      setError("تاریخ واردشده معتبر نیست");
      return; // به onChange چیزی پاس داده نمی‌شود تا سرور رد نکند
    }

    setError("");
    onChange?.(formatDate(y, m, d));
    setViewYear(y);
    setViewMonth(m);
  };

  // اگر کاربر فوکوس را از فیلد خارج کرد و مقدار نامعتبر مانده بود، پاکش کن
  const handleInputBlur = () => {
    if (!inputText) {
      setError("");
      return;
    }
    const normalized = toEn(inputText);
    const match = normalized.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (!match || !isValidJalali(+match[1], +match[2], +match[3])) {
      setError("تاریخ واردشده معتبر نبود و پاک شد");
      setInputText("");
      onChange?.("");
    }
  };

  /* ============== ساخت شبکه روزهای ماه ============== */
  const daysGrid = useMemo(() => {
    const daysInMonth = jalaliMonthDays(viewYear, viewMonth);

    const jToG = (jy, jm, jd) => {
      let jy2 = jy + 1595;
      let days =
        -355668 +
        365 * jy2 +
        ~~(jy2 / 33) * 8 +
        ~~(((jy2 % 33) + 3) / 4) +
        jd +
        (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
      let gy = 400 * ~~(days / 146097);
      days %= 146097;
      if (days > 36524) {
        gy += 100 * ~~(--days / 36524);
        days %= 36524;
        if (days >= 365) days++;
      }
      gy += 4 * ~~(days / 1461);
      days %= 1461;
      if (days > 365) {
        gy += ~~((days - 1) / 365);
        days = (days - 1) % 365;
      }
      let gd = days + 1;
      const sal_a = [
        0, 31, (gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0 ? 29 : 28,
        31, 30, 31, 30, 31, 31, 30, 31, 30, 31,
      ];
      let gm;
      for (gm = 0; gm < 13 && gd > sal_a[gm]; gm++) gd -= sal_a[gm];
      return { gy, gm, gd };
    };

    const { gy, gm, gd } = jToG(viewYear, viewMonth, 1);
    const firstDay = new Date(gy, gm - 1, gd);
    const jsDay = firstDay.getDay();
    const offset = (jsDay + 1) % 7; // 0=شنبه

    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewYear, viewMonth]);

  const isSelected = (day) => {
    if (!value) return false;
    const match = toEn(value).match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (!match) return false;
    return (
      +match[1] === viewYear && +match[2] === viewMonth && +match[3] === day
    );
  };

  const isToday = (day) =>
    today.jy === viewYear && today.jm === viewMonth && today.jd === day;

  const yearsList = useMemo(
    () => Array.from({ length: 16 }, (_, i) => (yearRangeStart ?? viewYear - 7) + i),
    [yearRangeStart, viewYear]
  );

  return (
    <div className="jdp-wrap" ref={wrapRef}>
      <div className={`jdp-input-box ${error ? "has-error" : ""}`}>
        <input
          type="text"
          className="jdp-input"
          value={inputText}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={openCalendar}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          name={name}
          inputMode="numeric"
          maxLength={10}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore
        />
        <button
          type="button"
          className="jdp-icon-btn"
          onClick={() => (open ? setOpen(false) : openCalendar())}
          tabIndex={-1}
          aria-label="باز کردن تقویم"
        >
          <i className="fa-regular fa-calendar" />
        </button>
      </div>

      {error && <div className="jdp-error">{error}</div>}

      {open && (
        <div className="jdp-popover">
          {/* هدر */}
          <div className="jdp-header">
            <button
              type="button"
              className="jdp-nav"
              onClick={goPrev}
              aria-label="قبلی"
            >
              <i className="fa-solid fa-chevron-right" />
            </button>

            <div className="jdp-title">
              {pickerMode === "years" ? (
                <span>انتخاب سال</span>
              ) : pickerMode === "months" ? (
                <button
                  type="button"
                  className="jdp-title-btn jdp-year"
                  onClick={openYearPicker}
                >
                  {toFa(viewYear)}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="jdp-title-btn jdp-month"
                    onClick={openMonthPicker}
                  >
                    {MONTHS[viewMonth - 1]}
                  </button>
                  <button
                    type="button"
                    className="jdp-title-btn jdp-year"
                    onClick={openYearPicker}
                  >
                    {toFa(viewYear)}
                  </button>
                </>
              )}
            </div>

            <button
              type="button"
              className="jdp-nav"
              onClick={goNext}
              aria-label="بعدی"
            >
              <i className="fa-solid fa-chevron-left" />
            </button>
          </div>

          {/* بدنه: روزها / ماه‌ها / سال‌ها */}
          {pickerMode === "days" && (
            <>
              <div className="jdp-weekdays">
                {WEEKDAYS.map((d, i) => (
                  <div key={i} className="jdp-weekday">
                    {d}
                  </div>
                ))}
              </div>

              <div className="jdp-grid">
                {daysGrid.map((day, i) =>
                  day === null ? (
                    <div key={i} className="jdp-cell jdp-empty" />
                  ) : (
                    <button
                      key={i}
                      type="button"
                      className={`jdp-cell jdp-day ${
                        isSelected(day) ? "selected" : ""
                      } ${isToday(day) ? "today" : ""}`}
                      onClick={() => selectDay(day)}
                    >
                      {toFa(day)}
                    </button>
                  )
                )}
              </div>
            </>
          )}

          {pickerMode === "months" && (
            <div className="jdp-monthgrid">
              {MONTHS.map((mn, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`jdp-monthcell ${
                    viewMonth === idx + 1 ? "selected" : ""
                  }`}
                  onClick={() => selectMonth(idx + 1)}
                >
                  {mn}
                </button>
              ))}
            </div>
          )}

          {pickerMode === "years" && (
            <div className="jdp-yeargrid">
              {yearsList.map((y) => (
                <button
                  key={y}
                  type="button"
                  className={`jdp-yearcell ${
                    viewYear === y ? "selected" : ""
                  }`}
                  onClick={() => selectYear(y)}
                >
                  {toFa(y)}
                </button>
              ))}
            </div>
          )}

          {/* فوتر */}
          <div className="jdp-footer">
            <button type="button" className="jdp-today-btn" onClick={goToday}>
              امروز
            </button>
            <button
              type="button"
              className="jdp-clear-btn"
              onClick={() => {
                setError("");
                onChange?.("");
                setInputText("");
                setOpen(false);
                setPickerMode("days");
              }}
            >
              پاک کردن
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
